/**
 * Project Service - CRUD operations for projects
 * Handles projects and project membership
 */

import { supabase } from '../../supabaseClient';
import { transformProjectFromDB, transformProjectToDB } from '../utils/transformers';

/**
 * Fetch all projects for current user
 */
export async function fetchAll() {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .is('deleted_at', null)
      .order('is_default', { ascending: false })
      .order('created_at', { ascending: true });

    if (error) throw error;

    return {
      success: true,
      data: data.map(transformProjectFromDB)
    };
  } catch (error) {
    console.error('[projectService.fetchAll] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Create a new project with client_request_id for idempotence
 */
export async function create(project, clientRequestId = null) {
  const requestId = clientRequestId || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  try {
    // Get current user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user');

    const { data, error } = await supabase
      .from('projects')
      .insert([{
        ...transformProjectToDB(project),
        owner_id: user.id,
        client_request_id: requestId
      }])
      .select()
      .single();

    if (error) {
      // Check for duplicate (unique violation on client_request_id)
      if (error.code === '23505' && error.message.includes('client_request_id')) {
        console.log('[projectService.create] Duplicate detected, fetching existing project');

        // Fetch existing project
        const { data: existing, error: fetchError } = await supabase
          .from('projects')
          .select('*')
          .eq('client_request_id', requestId)
          .single();

        if (fetchError) throw fetchError;

        return {
          success: true,
          data: transformProjectFromDB(existing),
          isDuplicate: true
        };
      }

      throw error;
    }

    // Note: project_members row is auto-created by trigger

    return {
      success: true,
      data: transformProjectFromDB(data)
    };
  } catch (error) {
    console.error('[projectService.create] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Update a project
 */
export async function update(projectId, updates) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update(transformProjectToDB(updates))
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;

    return {
      success: true,
      data: transformProjectFromDB(data)
    };
  } catch (error) {
    console.error('[projectService.update] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Soft delete a project (non-default only)
 */
export async function deleteProject(projectId) {
  try {
    // Check if it's the default project (should be blocked by trigger, but double-check)
    const { data: project } = await supabase
      .from('projects')
      .select('is_default')
      .eq('id', projectId)
      .single();

    if (project?.is_default) {
      return {
        success: false,
        error: 'Cannot delete default project'
      };
    }

    const { error } = await supabase
      .from('projects')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', projectId);

    if (error) throw error;

    return { success: true };
  } catch (error) {
    console.error('[projectService.delete] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get default project for current user
 */
export async function getDefault() {
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('is_default', true)
      .is('deleted_at', null)
      .single();

    if (error) throw error;

    return {
      success: true,
      data: transformProjectFromDB(data)
    };
  } catch (error) {
    console.error('[projectService.getDefault] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Set a project as default (will automatically unset others)
 */
export async function setDefault(projectId) {
  try {
    const { data, error } = await supabase
      .from('projects')
      .update({ is_default: true })
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;

    // Trigger will automatically set is_default=false on other projects

    return {
      success: true,
      data: transformProjectFromDB(data)
    };
  } catch (error) {
    console.error('[projectService.setDefault] Error:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

export const projectService = {
  fetchAll,
  create,
  update,
  delete: deleteProject,
  getDefault,
  setDefault
};
